import { type WhiteboardData, type LayoutOptions } from '../../types';
import Diagram, { type RenderToWhiteboardOptions } from '../../common/Diagram';
import {
  type DiagramDatabase,
  DiagramNode,
} from '../../parsing/DiagramStructure';
import paper from 'paper';
import { randomId } from '../../common/utils';
import { whiteboardObjectForNode } from '../../common/whiteboardUtils';
import properties from './properties';
import description from './description';

class HierarchyDiagram extends Diagram {
  static diagramName = 'Hierarchy';
  static diagramType = 'hierarchy';

  static description = description;
  static diagramProperties = properties;
  static allowedProperties = {
    ...Diagram.allowedProperties,
    ...this.diagramProperties,
  };
  detect(): boolean {
    throw new Error('Method not implemented.');
  }

  calculateLabelWidth(label: string): number {
    // Approximate width calculation: ~8 pixels per character + padding
    return Math.max(50, label.length * 8 + 20);
  }

  cardinalDirection(direction: string): paper.Point {
    switch (direction) {
      case 'north':
        return new paper.Point({ x: 0, y: -1 });
      case 'south':
        return new paper.Point({ x: 0, y: 1 });
      case 'east':
        return new paper.Point({ x: 1, y: 0 });
      case 'west':
        return new paper.Point({ x: -1, y: 0 });
      default:
        throw new Error(`Unknown direction: ${direction}`);
    }
  }

  orientationTransform(orientation: string, x: number, y: number): paper.Point {
    const direction = this.cardinalDirection(orientation);
    return new paper.Point(x * direction.x, y * direction.y);
  }

  computeOffset(
    childBoundary: paper.PathItem,
    boundary: paper.PathItem,
    nodeGap: number,
    orientation: string,
    childLayoutDirection: string
  ): paper.Point {
    let layout;

    if (orientation === 'north' || orientation === 'south') {
      layout =
        childLayoutDirection === 'perpendicular' ? 'vertical' : 'horizontal';
    } else {
      layout =
        childLayoutDirection === 'perpendicular' ? 'horizontal' : 'vertical';
    }

    if (layout === 'horizontal') {
      if (childBoundary.bounds.left < 0) {
        return new paper.Point(-childBoundary.bounds.left, 0);
      }

      const overlap = childBoundary.intersect(boundary, { insert: false })
        .bounds.width;
      return new paper.Point(overlap + (overlap ? nodeGap : 0), 0);
    } else {
      if (childBoundary.bounds.top < 0) {
        return new paper.Point(0, -childBoundary.bounds.top);
      }

      const overlap = childBoundary.intersect(boundary, { insert: false })
        .bounds.height;
      return new paper.Point(0, overlap + (overlap ? nodeGap : 0));
    }
  }

  // Based on the orientation set the connection points for the node.
  setParentPorts(node: DiagramNode, orientation: string) {
    switch (orientation) {
      case 'north':
        node.layout.outlet = 'ct';
        break;
      case 'south':
        node.layout.outlet = 'cb';
        break;
      case 'east':
        node.layout.outlet = 'cr';
        break;
      case 'west':
        node.layout.outlet = 'cl';
        break;
    }
  }

  setChildPorts(
    node: DiagramNode,
    orientation: string,
    childLayoutLocation: string | number,
    childLayoutDirection: string
  ) {
    switch (orientation) {
      case 'north':
        node.layout.inlet = 'cb';
        break;
      case 'south':
        if (childLayoutDirection === 'normal') {
          node.layout.inlet = 'ct';
        } else {
          switch (childLayoutLocation) {
            case 'offsetBefore':
              node.layout.inlet = 'cl';
              break;
            case 'offsetAfter':
              node.layout.inlet = 'cr';
              break;
            default:
              node.layout.inlet = 'ct';
          }
        }
        break;
      case 'east':
        node.layout.inlet = 'cl';
        break;
      case 'west':
        node.layout.inlet = 'cr';
        break;
    }
  }

  // Position the node and its children relative to origin.
  //
  // As we lay out the children (and their children) build up polygons representing
  // the boundary of these nodes. By using a polygonal bounding box we can
  // position the nodes in a way that prevents overlap, but also alows a visually
  // pleasing compact layout.
  //
  // Returns the bounding polygon of node and its children.
  async layoutChild(
    node: DiagramNode | DiagramDatabase,
    origin: paper.Point, // The origin of where this node and its children should be placed.
    depth: number,
    layoutOptions: LayoutOptions
  ): Promise<{ boundary: paper.PathItem /* ; lastLayer: paper.Rectangle */ }> {
    const orientation = this.getNodeProperty(node, 'orientation') as string;
    const childLayoutLocation = this.getNodeProperty(
      node,
      'childLayoutLocation'
    );
    const childLayoutDirection = this.getNodeProperty(
      node,
      'childLayoutDirection'
    ) as string;
    const rankGap = this.getNodeProperty(node, 'rankGap') as number;
    let boundary: paper.PathItem = new paper.Path({ insert: false });
    const direction =
      orientation === 'north' || orientation === 'south'
        ? 'horizontal'
        : 'vertical';

    if (node instanceof DiagramNode) {
      // Initial position this node. It will be adjusted after the children are placed.
      await this.sizeNode(node, layoutOptions);
      node.layout.left = origin.x;
      node.layout.top = origin.y;
      node.layout.depth = depth - 1;

      this.setParentPorts(node, orientation);

      // Position the children relative to this node.
      origin = origin.add(
        this.orientationTransform(
          orientation,
          node.layout.width + rankGap,
          node.layout.height + rankGap
        )
      );
    }

    // Position the children.
    const minPosition = new paper.Point(origin);
    for (const childId of node.children) {
      const child = this.database.node(childId);
      const nodeGap = this.getNodeProperty(child, 'nodeGap') as number;

      // We want to pack the children as tightly as possible, but without
      // reordering. So start with a minimum left or top based on the last child.
      if (orientation === 'north' || orientation === 'south') {
        origin.x = minPosition.x;
      } else {
        origin.y = minPosition.y;
      }
      const { boundary: childBoundary } = await this.layoutChild(
        child,
        new paper.Point(origin.x, origin.y),
        depth + 1,
        layoutOptions
      );

      this.setChildPorts(
        child,
        orientation,
        childLayoutLocation,
        childLayoutDirection
      );

      // Make sure that the new children don't overlap the previously placed
      // child and also {childLayoutLocation: offsetBefore} can create negative
      // left values which we need to correct.
      let offset: paper.Point;
      while (
        !(offset = this.computeOffset(
          childBoundary,
          boundary,
          nodeGap,
          orientation,
          childLayoutDirection
        )).isZero()
      ) {
        origin = origin.add(offset);

        const moveChildren = (node: DiagramNode, x: number, y: number) => {
          node.layout.left += x;
          node.layout.top += y;
          for (const childId of node.children) {
            moveChildren(this.database.node(childId), x, y);
          }
        };
        moveChildren(child, offset.x, offset.y);

        childBoundary.translate(offset);
      }
      if (orientation === 'north' || orientation === 'south') {
        minPosition.x = child.layout.left;
      } else {
        minPosition.y = child.layout.top;
      }

      boundary = boundary.unite(childBoundary, { insert: false });
    }

    if (!(node instanceof DiagramNode)) {
      return { boundary };
    }

    if (node.children.length > 0) {
      // Position the parent relative to the children
      switch (childLayoutLocation) {
        case 'center':
          if (direction === 'horizontal') {
            node.layout.left = boundary.bounds.center.x - node.layout.width / 2;
          } else {
            node.layout.top = boundary.bounds.center.y - node.layout.height / 2;
          }
          break;
        case 'start':
          if (direction === 'horizontal') {
            node.layout.left = boundary.bounds.left;
          } else {
            node.layout.top = boundary.bounds.top;
          }
          break;
        case 'end':
          if (direction === 'horizontal') {
            node.layout.left =
              boundary.bounds.left + boundary.bounds.width - node.layout.width;
          } else {
            node.layout.top =
              boundary.bounds.top + boundary.bounds.height - node.layout.height;
          }
          break;
        case 'offsetBefore':
          if (direction === 'horizontal') {
            node.layout.left = boundary.bounds.left - 0.75 * node.layout.width;
          } else {
            node.layout.top = boundary.bounds.top - 0.75 * node.layout.height;
          }
          break;
        case 'offsetAfter':
          if (direction === 'horizontal') {
            node.layout.left =
              boundary.bounds.left +
              boundary.bounds.width -
              node.layout.width / 4;
          } else {
            node.layout.top =
              boundary.bounds.top +
              boundary.bounds.height -
              node.layout.height / 4;
          }
          break;
      }
    } else {
      // No children, so leave this node where it is.
    }

    boundary = boundary.unite(
      new paper.Path.Rectangle({
        insert: false,
        x: node.layout.left,
        y: node.layout.top,
        width: node.layout.width,
        height: node.layout.height,
      }),
      { insert: false }
    );

    return { boundary };
  }

  async layout(layoutOptions: LayoutOptions) {
    // We use paper for layout, so make sure it is set up.
    if (!paper.project) {
      // @ts-expect-error Paper's types are wrong, it's valid to call this with no arguments - see Project constructor
      paper.setup();
    }

    // Start with a default size in case there are no nodes.
    this.database.layout.width = 250;
    this.database.layout.height = 250;

    // Traverse depth-first, post-order, computing the size of each node and positioning it.
    const { boundary } = await this.layoutChild(
      this.database,
      new paper.Point(0, 0),
      0,
      layoutOptions
    );

    this.database.layout.width = boundary.bounds.width;
    this.database.layout.height = boundary.bounds.height;
  }

  renderToWhiteboard(options?: RenderToWhiteboardOptions): any {
    const { nodeAttrs, edgeAttrs } = options || {};
    const whiteboard: WhiteboardData = {
      version: '1.2.6',
      objects: {},
    };
    const colorScheme = this.getColorScheme();

    // Create edges between nodes.
    const edgesForNode = (nodeId: string) => {
      const node = this.database.node(nodeId);

      for (const childId of node.children) {
        const child = this.database.node(childId);
        const edgeId = randomId();

        const connectorColor = colorScheme.connectorColor({
          depth: node.layout.depth || 0,
        });

        const edgeAttrsUnpacked = edgeAttrs?.(node, child) || {};

        whiteboard.objects[edgeId] = {
          type: 'connector',
          id: edgeId,
          lineRouting: 20,
          stroke: connectorColor.stroke,
          strokeWidth: connectorColor.strokeWidth,
          attachments: [
            {
              objectId: nodeId,
              attachmentPoint: node.layout.outlet,
            },
            {
              objectId: childId,
              attachmentPoint: child.layout.inlet,
            },
          ],
          endStyles: [0, 0],
          points: [
            { x: 0, y: 0 },
            { x: 100, y: 100 },
          ],
          ...edgeAttrsUnpacked,
        };

        // Add label if there is one for this node
        if (edgeAttrsUnpacked?.label) {
          const labelId = randomId();
          const labelWidth = this.calculateLabelWidth(edgeAttrsUnpacked.label);

          whiteboard.objects[labelId] = {
            type: 'shape',
            id: labelId,
            shapeName: 'text',
            textContent: `<p><em><span style="color:${
              edgeAttrsUnpacked.stroke || connectorColor.stroke
            }">${edgeAttrsUnpacked.label}</span></em></p>`,
            height: 30,
            width: labelWidth,
            scaleX: 1,
            scaleY: 1,
            fontSize: 14,
            connectorId: edgeId,
            labelOrientation: 'inline',
            labelPosition: edgeAttrsUnpacked.labelPosition ?? 0.5,
          };
        }

        edgesForNode(childId);
      }
    };
    for (const id of this.database.children) {
      edgesForNode(id);
    }

    // Add the nodes. Second so they are in front of the connectors.
    const addNodes = (children: string[], depth: number) => {
      for (let i = 0; i < children.length; i++) {
        const id = children[i];
        const node = this.database.node(id);

        if (node.layout.left === undefined) continue;

        whiteboardObjectForNode(
          whiteboard,
          this,
          node,
          {
            n: i,
            total: children.length,
            depth,
          },
          nodeAttrs?.(node)
        );
        addNodes(node.children, depth + 1);
      }
    };
    addNodes(this.database.children, 0);

    return whiteboard;
  }
}

export default HierarchyDiagram;
